(() => {
  const API_REVISION = '2026-07-15';
  const ROOT_SELECTOR = '[data-pl-golden-pig]';

  const safeJson = (node, fallback) => {
    try {
      return JSON.parse(node?.textContent || '');
    } catch (error) {
      console.error('[PORCE LAINE] Cochon d’or JSON invalide', error);
      return fallback;
    }
  };

  const escapeHtml = (value = '') => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const replacePrizeToken = (value = '', prizeLabel = '') => {
    return String(value).replace(/\[prize\]/gi, prizeLabel);
  };

  const randomBetween = (min, max) => {
    const low = Math.min(Number(min) || 0, Number(max) || 0);
    const high = Math.max(Number(min) || 0, Number(max) || 0);
    return low + Math.random() * (high - low);
  };

  const initGoldenPig = (root) => {
    if (!root || root.dataset.plGoldenReady === 'true') return;
    root.dataset.plGoldenReady = 'true';

    const config = safeJson(root.querySelector('[data-pl-golden-config]'), {});
    const prizesRaw = safeJson(root.querySelector('[data-pl-golden-prizes]'), []);
    const prizes = Array.isArray(prizesRaw)
      ? prizesRaw
          .map((prize, index) => ({
            id: prize.id || `prize-${index + 1}`,
            key: String(prize.key || '').trim() || `prize-${index + 1}`,
            label: String(prize.label || '').trim() || 'une surprise',
            weight: Math.max(1, Number(prize.weight) || 1)
          }))
      : [];

    const runner = root.querySelector('[data-pl-golden-runner]');
    const dialog = root.querySelector('[data-pl-golden-dialog]');
    const closeButton = root.querySelector('[data-pl-golden-close]');
    const formStage = root.querySelector('[data-pl-golden-form-stage]');
    const successStage = root.querySelector('[data-pl-golden-success-stage]');
    const form = root.querySelector('[data-pl-golden-form]');
    const emailInput = root.querySelector('[data-pl-golden-email]');
    const consentInput = root.querySelector('[data-pl-golden-consent]');
    const submitButton = root.querySelector('[data-pl-golden-submit]');
    const errorNode = root.querySelector('[data-pl-golden-error]');
    const successTitle = root.querySelector('[data-pl-golden-success-title]');
    const successText = root.querySelector('[data-pl-golden-success-text]');
    const prizeLabelNode = root.querySelector('[data-pl-golden-prize-label]');
    const successClose = root.querySelector('[data-pl-golden-success-close]');

    if (!runner || !dialog || !form || !emailInput || !submitButton) return;

    const designMode = Boolean(window.Shopify && window.Shopify.designMode);
    const testMode = Boolean(config.testMode || designMode);

    if (!config.enabled && !designMode) return;

    const excludedPaths = String(config.excludedPaths || '')
      .split(/\r?\n/)
      .map((item) => item.trim())
      .filter(Boolean);

    if (!designMode && excludedPaths.some((path) => window.location.pathname.startsWith(path))) {
      return;
    }

    const namespace = `pl_golden_pig_${root.id || 'global'}`;
    const sessionCountKey = `${namespace}_appearances`;
    const claimedKey = `${namespace}_claimed`;
    const pendingPrizeKey = `${namespace}_pending_prize`;

    let isVisible = false;
    let appearanceTimer = null;
    let hideTimer = null;
    let wanderTimer = null;
    let selectedPrize = null;

    const readSessionCount = () => {
      if (testMode) return 0;
      try {
        return Number(sessionStorage.getItem(sessionCountKey) || 0);
      } catch (_) {
        return 0;
      }
    };

    const writeSessionCount = (value) => {
      if (testMode) return;
      try {
        sessionStorage.setItem(sessionCountKey, String(value));
      } catch (_) {}
    };

    const readClaim = () => {
      if (testMode) return null;
      try {
        return JSON.parse(localStorage.getItem(claimedKey) || 'null');
      } catch (_) {
        return null;
      }
    };

    const hasActiveCooldown = () => {
      const claim = readClaim();
      if (!claim?.claimedAt) return false;
      const claimedAt = new Date(claim.claimedAt).getTime();
      if (!Number.isFinite(claimedAt)) return false;
      const cooldownMs = Math.max(1, Number(config.cooldownDays) || 30) * 86400000;
      return Date.now() - claimedAt < cooldownMs;
    };

    const canAppear = () => {
      if (testMode) return true;
      if (hasActiveCooldown()) return false;
      return readSessionCount() < Math.max(1, Number(config.maxAppearances) || 1);
    };

    const getScrollPercent = () => {
      const doc = document.documentElement;
      const scrollable = Math.max(0, doc.scrollHeight - window.innerHeight);
      if (scrollable <= 0) return 100;
      return Math.min(100, Math.max(0, (window.scrollY / scrollable) * 100));
    };

    const pickPrize = () => {
      if (!prizes.length) {
        return { id: 'surprise', key: 'surprise', label: 'une surprise', weight: 1 };
      }

      if (!testMode) {
        try {
          const pendingKey = sessionStorage.getItem(pendingPrizeKey);
          const pending = prizes.find((prize) => prize.key === pendingKey);
          if (pending) return pending;
        } catch (_) {}
      }

      const totalWeight = prizes.reduce((total, prize) => total + prize.weight, 0);
      let cursor = Math.random() * totalWeight;
      let picked = prizes[prizes.length - 1];

      for (const prize of prizes) {
        cursor -= prize.weight;
        if (cursor <= 0) {
          picked = prize;
          break;
        }
      }

      if (!testMode) {
        try {
          sessionStorage.setItem(pendingPrizeKey, picked.key);
        } catch (_) {}
      }

      return picked;
    };

    const runnerSize = () => {
      const rect = runner.getBoundingClientRect();
      return {
        width: rect.width || 80,
        height: rect.height || 80
      };
    };

    const randomPosition = () => {
      const { width, height } = runnerSize();
      const padding = window.innerWidth < 750 ? 10 : 18;
      const maxX = Math.max(padding, window.innerWidth - width - padding);
      const maxY = Math.max(padding, window.innerHeight - height - padding);

      return {
        x: randomBetween(padding, maxX),
        y: randomBetween(padding, maxY)
      };
    };

    const moveRunner = (instant = false) => {
      if (!isVisible) return;
      const { x, y } = randomPosition();
      if (instant) runner.style.transitionDuration = '0s';
      runner.style.transform = `translate3d(${Math.round(x)}px, ${Math.round(y)}px, 0)`;
      if (instant) {
        requestAnimationFrame(() => {
          runner.style.transitionDuration = '';
        });
      }
    };

    const stopWandering = () => {
      if (wanderTimer) clearInterval(wanderTimer);
      wanderTimer = null;
    };

    const startWandering = () => {
      stopWandering();
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const seconds = Math.max(2, Number(config.wanderInterval) || 5);
      wanderTimer = window.setInterval(() => moveRunner(false), seconds * 1000);
    };

    const hideRunner = ({ reschedule = true } = {}) => {
      if (!isVisible) return;
      isVisible = false;
      runner.classList.remove('is-visible');
      runner.setAttribute('aria-hidden', 'true');
      stopWandering();
      if (hideTimer) clearTimeout(hideTimer);
      hideTimer = null;

      window.setTimeout(() => {
        if (!isVisible) runner.hidden = true;
      }, 260);

      if (reschedule && canAppear()) scheduleAppearance();
    };

    const showRunner = () => {
      if (!canAppear() || isVisible || dialog.open) return;

      if (!testMode && getScrollPercent() < Math.max(0, Number(config.minScroll) || 0)) {
        appearanceTimer = window.setTimeout(showRunner, 900);
        return;
      }

      writeSessionCount(readSessionCount() + 1);
      runner.hidden = false;
      runner.removeAttribute('aria-hidden');
      isVisible = true;
      moveRunner(true);
      requestAnimationFrame(() => runner.classList.add('is-visible'));
      startWandering();

      if (hideTimer) clearTimeout(hideTimer);
      hideTimer = window.setTimeout(() => {
        hideRunner({ reschedule: true });
      }, Math.max(4, Number(config.visibleDuration) || 16) * 1000);

      window.dataLayer?.push?.({
        event: 'golden_pig_appeared',
        page_path: window.location.pathname
      });
    };

    const scheduleAppearance = () => {
      if (!canAppear()) return;
      if (appearanceTimer) clearTimeout(appearanceTimer);

      const delay = testMode
        ? 500
        : randomBetween(Math.max(1, Number(config.minDelay) || 1), Math.max(2, Number(config.maxDelay) || 2)) * 1000;

      appearanceTimer = window.setTimeout(showRunner, delay);
    };

    const openDialog = () => {
      selectedPrize = pickPrize();
      hideRunner({ reschedule: false });

      if (formStage) formStage.hidden = false;
      if (successStage) successStage.hidden = true;
      if (errorNode) {
        errorNode.textContent = '';
        errorNode.hidden = true;
      }

      if (typeof dialog.showModal === 'function') {
        if (!dialog.open) dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }

      window.setTimeout(() => emailInput.focus(), 60);

      window.dataLayer?.push?.({
        event: 'golden_pig_clicked',
        page_path: window.location.pathname
      });
    };

    const closeDialog = ({ reschedule = true } = {}) => {
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');

      if (reschedule && canAppear()) {
        window.setTimeout(scheduleAppearance, 1200);
      }
    };

    const setError = (message = '') => {
      if (!errorNode) return;
      errorNode.textContent = message;
      errorNode.hidden = !message;
    };

    const setSubmitting = (loading) => {
      submitButton.disabled = loading;
      submitButton.classList.toggle('is-loading', loading);
      emailInput.disabled = loading;
      if (consentInput) consentInput.disabled = loading;
    };

    const subscribeToKlaviyo = async ({ email, prize }) => {
      const publicKey = String(config.klaviyoPublicKey || '').trim();
      const listId = String(config.klaviyoListId || '').trim();

      if (!publicKey || !listId) {
        throw new Error('Configuration Klaviyo incomplète.');
      }

      const body = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: String(config.klaviyoSource || 'Cochon d’or - Site'),
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                  locale: document.documentElement.lang || undefined,
                  subscriptions: {
                    email: {
                      marketing: {
                        consent: 'SUBSCRIBED'
                      }
                    }
                  },
                  properties: {
                    'PL Golden Pig Prize': prize.key,
                    'PL Golden Pig Prize Label': prize.label,
                    'PL Golden Pig Page': window.location.href,
                    'PL Golden Pig Found At': new Date().toISOString(),
                    'PL Golden Pig Campaign': String(config.klaviyoSource || 'Cochon d’or - Site')
                  }
                }
              }
            }
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: listId
              }
            }
          }
        }
      };

      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 12000);

      try {
        const response = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(publicKey)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/vnd.api+json',
            Accept: 'application/vnd.api+json',
            revision: API_REVISION
          },
          body: JSON.stringify(body),
          signal: controller.signal
        });

        if (response.status !== 202) {
          let detail = '';
          try {
            const payload = await response.json();
            detail = payload?.errors?.[0]?.detail || '';
          } catch (_) {}
          throw new Error(detail || `Klaviyo a répondu ${response.status}.`);
        }

        return true;
      } finally {
        clearTimeout(timeout);
      }
    };

    const revealSuccess = (prize) => {
      const label = prize?.label || 'une surprise';
      if (formStage) formStage.hidden = true;
      if (successStage) successStage.hidden = false;
      if (prizeLabelNode) prizeLabelNode.textContent = label;
      if (successTitle) successTitle.textContent = replacePrizeToken(config.successTitle || 'Tu as gagné [prize] !', label);
      if (successText) successText.innerHTML = replacePrizeToken(config.successText || '', escapeHtml(label));
    };

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      setError('');

      const email = String(emailInput.value || '').trim();
      if (!emailInput.checkValidity()) {
        emailInput.reportValidity();
        return;
      }
      if (consentInput && !consentInput.checked) {
        consentInput.reportValidity();
        return;
      }

      const prize = selectedPrize || pickPrize();
      setSubmitting(true);

      try {
        await subscribeToKlaviyo({ email, prize });

        if (!testMode) {
          try {
            localStorage.setItem(claimedKey, JSON.stringify({
              claimedAt: new Date().toISOString(),
              prizeKey: prize.key,
              prizeLabel: prize.label
            }));
            sessionStorage.removeItem(pendingPrizeKey);
          } catch (_) {}
        }

        revealSuccess(prize);
        window.dataLayer?.push?.({
          event: 'golden_pig_claimed',
          golden_pig_prize: prize.key,
          page_path: window.location.pathname
        });
      } catch (error) {
        console.error('[PORCE LAINE] Erreur Cochon d’or', error);
        const message = error?.name === 'AbortError'
          ? 'La connexion a pris trop de temps. Réessaie dans quelques secondes.'
          : 'Impossible de valider ta participation pour le moment. Réessaie.';
        setError(message);
      } finally {
        setSubmitting(false);
      }
    });

    runner.addEventListener('click', openDialog);
    closeButton?.addEventListener('click', () => closeDialog({ reschedule: true }));
    successClose?.addEventListener('click', () => closeDialog({ reschedule: false }));

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog({ reschedule: true });
    });

    dialog.addEventListener('close', () => {
      if (!hasActiveCooldown() && canAppear()) scheduleAppearance();
    });

    document.addEventListener('shopify:section:load', (event) => {
      const newRoot = event.target?.querySelector?.(ROOT_SELECTOR);
      if (newRoot && newRoot !== root) initGoldenPig(newRoot);
    });

    window.addEventListener('resize', () => {
      if (isVisible) moveRunner(true);
    }, { passive: true });

    scheduleAppearance();
  };

  const boot = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initGoldenPig);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
